/**
 * SuggestionRow — one "People you might know" recommendation.
 *
 * Renders the person's real avatar (FriendAvatar), display name, a reason line built from the
 * two graph signals, a one-tap Add, and a dismiss control. Add is optimistic: the row shows
 * "Sent" immediately (no reflow) and reverts if the parent's onAdd resolves false.
 */

import React, { useState, useCallback } from 'react'
import { View, Text } from 'react-native'
import { UserPlus, Check, X, Users } from 'lucide-react-native'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import { FriendAvatar } from '../shared/FriendAvatar'
import type { FriendSuggestion } from '../../../src/friends/types/friends.types'
import {
  FRIENDS_SUGGESTIONS_TEST_IDS,
  FRIENDS_SUGGESTIONS_COPY,
} from '../../../src/friends/constants/suggestions.constants'
import { buildSuggestionReason } from './SuggestionRow.helper'
import {
  suggestionRowStyles as styles,
  SUGGESTION_AVATAR_SIZE,
  SUGGESTION_ROW_COLORS as C,
} from './SuggestionRow.styles'

export interface SuggestionRowProps {
  suggestion: FriendSuggestion
  /** Sends the friend request. Resolve false to revert the optimistic "Sent" state. */
  onAdd: (suggestion: FriendSuggestion) => Promise<boolean>
  onDismiss: (id: string) => void
  testID?: string
}

export const SuggestionRow: React.FC<SuggestionRowProps> = ({
  suggestion,
  onAdd,
  onDismiss,
  testID,
}) => {
  const [requested, setRequested] = useState(false)
  const [pending, setPending] = useState(false)
  const reason = buildSuggestionReason(suggestion.mutualFriendCount, suggestion.sharedSquadOrgCount)

  const handleAdd = useCallback(async () => {
    if (requested || pending) return
    setPending(true)
    setRequested(true) // optimistic — no reflow
    const ok = await onAdd(suggestion)
    if (!ok) setRequested(false)
    setPending(false)
  }, [requested, pending, onAdd, suggestion])

  return (
    <View style={styles.row} testID={testID ?? FRIENDS_SUGGESTIONS_TEST_IDS.ROW}>
      <View style={styles.avatarZone}>
        <FriendAvatar
          avatarId={suggestion.avatarId}
          avatarConfig={suggestion.avatarConfig}
          fallbackUrl={suggestion.avatar ?? undefined}
          size={SUGGESTION_AVATAR_SIZE}
          nameForInitials={suggestion.name}
          avatarRounded={false}
        />
      </View>

      <View style={styles.info}>
        <Text style={styles.name} numberOfLines={1}>
          {suggestion.name}
        </Text>
        <View style={styles.reasonRow}>
          <Users size={13} color={C.dim} />
          <Text style={styles.reasonText} numberOfLines={1}>
            {reason}
          </Text>
        </View>
      </View>

      <View style={styles.actions}>
        <SoundPressable
          tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
          onPress={handleAdd}
          disabled={requested || pending}
          accessibilityRole="button"
          accessibilityLabel={requested ? `Request sent to ${suggestion.name}` : `Add ${suggestion.name}`}
          style={[styles.addButton, requested && styles.addedButton]}
          testID={FRIENDS_SUGGESTIONS_TEST_IDS.ADD_BUTTON}
        >
          {requested ? (
            <>
              <Check size={14} color={C.mint} />
              <Text style={[styles.addButtonLabel, styles.addedButtonLabel]}>
                {FRIENDS_SUGGESTIONS_COPY.ADDED}
              </Text>
            </>
          ) : (
            <>
              <UserPlus size={14} color={C.onLavender} />
              <Text style={styles.addButtonLabel}>{FRIENDS_SUGGESTIONS_COPY.ADD}</Text>
            </>
          )}
        </SoundPressable>

        {!requested && (
          <SoundPressable
            tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
            onPress={() => onDismiss(suggestion.id)}
            accessibilityRole="button"
            accessibilityLabel={`Dismiss ${suggestion.name}`}
            style={styles.dismissButton}
            testID={FRIENDS_SUGGESTIONS_TEST_IDS.DISMISS_BUTTON}
          >
            <X size={16} color={C.dim} />
          </SoundPressable>
        )}
      </View>
    </View>
  )
}
